import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Award,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  GraduationCap,
  Percent,
  TrendingUp,
  Users,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import '../../styles/module-shared.css';
import './education.css';
import { formatCourseDate } from './courseUtils';

const THEME = getThemeColor('primary');

const PERIODS = [
  { value: 'AllTime', label: 'All time' },
  { value: 'Last7Days', label: 'Last 7 days' },
  { value: 'Last30Days', label: 'Last 30 days' },
  { value: 'Last90Days', label: 'Last 90 days' },
  { value: 'Custom', label: 'Custom' },
];

const LEARNER_STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'NotStarted', label: 'Not started' },
  { value: 'InProgress', label: 'In progress' },
  { value: 'Completed', label: 'Completed' },
];

const emptySummary = {
  totalCourses: 0,
  activeCourses: 0,
  totalLearners: 0,
  completedCourses: 0,
  averageProgress: 0,
  completionRate: 0,
  quizPassRate: 0,
  certificatesIssued: 0,
};

const emptyPage = () => ({
  pageNumber: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasPreviousPage: false,
  hasNextPage: false,
});

const unwrapPayload = (res) => res?.data ?? res?.result ?? {};

const unwrapList = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const applyPageMeta = (prev, payload, pageNumber) => ({
  ...prev,
  pageNumber: payload.pageNumber || pageNumber,
  pageSize: payload.pageSize || prev.pageSize,
  totalRecords: payload.totalRecords || 0,
  totalPages: payload.totalPages || 0,
  hasPreviousPage: Boolean(payload.hasPreviousPage),
  hasNextPage: Boolean(payload.hasNextPage),
});

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}%`;
};

const formatNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString();
};

const toApiDate = (value) => {
  if (!value) return null;
  return `${value}T00:00:00`;
};

const learnerStatusClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return 'is-active';
  if (s === 'inprogress') return 'is-progress';
  return 'is-inactive';
};

const PaginationBar = ({ pagination, loading, onPage }) => (
  <div className="edu-pagination">
    <span>
      Showing page {pagination.pageNumber} of {Math.max(pagination.totalPages, 1)} (
      {pagination.totalRecords} total)
    </span>
    <div className="edu-pagination__btns">
      <button
        type="button"
        className="edu-btn edu-btn--ghost edu-btn--sm"
        disabled={!pagination.hasPreviousPage || loading}
        onClick={() => onPage(pagination.pageNumber - 1)}
      >
        Previous
      </button>
      <button
        type="button"
        className="edu-btn edu-btn--ghost edu-btn--sm"
        disabled={!pagination.hasNextPage || loading}
        onClick={() => onPage(pagination.pageNumber + 1)}
      >
        Next
      </button>
    </div>
  </div>
);

const EmptyBlock = ({ icon: Icon, message }) => (
  <div className="edu-empty">
    <Icon className="edu-empty__icon" />
    <p>{message}</p>
  </div>
);

const EducationAnalytics = () => {
  const { showError } = useToast();
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState('');

  const [period, setPeriod] = useState('AllTime');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [summary, setSummary] = useState(emptySummary);
  const [popularCourses, setPopularCourses] = useState([]);
  const [highestCompletion, setHighestCompletion] = useState([]);
  const [quizSummary, setQuizSummary] = useState(null);

  const [courseRows, setCourseRows] = useState([]);
  const [coursePage, setCoursePage] = useState(emptyPage);
  const [courseLoading, setCourseLoading] = useState(false);

  const [learnerRows, setLearnerRows] = useState([]);
  const [learnerPage, setLearnerPage] = useState(emptyPage);
  const [learnerLoading, setLearnerLoading] = useState(false);
  const [learnerSearchInput, setLearnerSearchInput] = useState('');
  const [learnerSearch, setLearnerSearch] = useState('');
  const [learnerCourseId, setLearnerCourseId] = useState('');
  const [learnerStatus, setLearnerStatus] = useState('');

  const [quizRows, setQuizRows] = useState([]);
  const [quizPage, setQuizPage] = useState(emptyPage);
  const [quizLoading, setQuizLoading] = useState(false);

  const [activityRows, setActivityRows] = useState([]);
  const [activityPage, setActivityPage] = useState(emptyPage);
  const [activityLoading, setActivityLoading] = useState(false);

  const dateFilter = useMemo(() => {
    const isCustom = period === 'Custom';
    return {
      period: period || 'AllTime',
      fromDate: isCustom ? toApiDate(fromDate) : null,
      toDate: isCustom ? toApiDate(toDate) : null,
    };
  }, [period, fromDate, toDate]);

  const courseOptions = useMemo(() => {
    const map = new Map();
    courseRows.forEach((row) => {
      if (row?.courseId != null && !map.has(row.courseId)) {
        map.set(row.courseId, row.courseName || `Course #${row.courseId}`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [courseRows]);

  const fetchSummaryAndPanels = useCallback(async () => {
    const [summaryRes, popularRes, completionRes] = await Promise.all([
      apiService.getEducationAnalyticsSummary(dateFilter),
      apiService.getEducationPopularCourses(dateFilter),
      apiService.getEducationCompletionAnalytics(dateFilter),
    ]);

    const errors = [];

    if (summaryRes.success) {
      const raw = unwrapPayload(summaryRes) || {};
      setSummary({
        totalCourses: Number(raw.totalCourses) || 0,
        activeCourses: Number(raw.activeCourses) || 0,
        totalLearners: Number(raw.totalLearners) || 0,
        completedCourses: Number(raw.completedCourses) || 0,
        averageProgress: Number(raw.averageProgress) || 0,
        completionRate: Number(raw.completionRate) || 0,
        quizPassRate: Number(raw.quizPassRate) || 0,
        certificatesIssued: Number(raw.certificatesIssued) || 0,
      });
    } else {
      setSummary(emptySummary);
      errors.push(summaryRes.error || summaryRes.message || 'Failed to load summary');
    }

    if (popularRes.success) {
      const list = unwrapList(unwrapPayload(popularRes));
      setPopularCourses(list);
    } else {
      setPopularCourses([]);
      errors.push(popularRes.error || popularRes.message || 'Failed to load popular courses');
    }

    if (completionRes.success) {
      const extras = unwrapPayload(completionRes) || {};
      setHighestCompletion(
        Array.isArray(extras.highestCompletion) ? extras.highestCompletion : []
      );
      setQuizSummary(extras.quizSummary || null);
    } else {
      setHighestCompletion([]);
      setQuizSummary(null);
      errors.push(completionRes.error || completionRes.message || 'Failed to load completion analytics');
    }

    return errors;
  }, [dateFilter]);

  const fetchCourseAnalytics = useCallback(
    async (pageNumber = 1) => {
      setCourseLoading(true);
      try {
        const res = await apiService.getEducationCourseAnalytics({
          ...dateFilter,
          pageNumber,
          pageSize: coursePage.pageSize,
        });
        if (!res.success) {
          setCourseRows([]);
          setCoursePage((prev) => ({ ...prev, ...emptyPage(), pageSize: prev.pageSize }));
          showError(res.error || res.message || 'Failed to load course analytics');
          return;
        }
        const payload = unwrapPayload(res);
        setCourseRows(unwrapList(payload));
        setCoursePage((prev) => applyPageMeta(prev, payload, pageNumber));
      } catch (err) {
        setCourseRows([]);
        showError(err.message || 'Failed to load course analytics');
      } finally {
        setCourseLoading(false);
      }
    },
    [dateFilter, coursePage.pageSize, showError]
  );

  const fetchLearnerAnalytics = useCallback(
    async (pageNumber = 1) => {
      setLearnerLoading(true);
      try {
        const res = await apiService.getEducationLearnerAnalytics({
          ...dateFilter,
          pageNumber,
          pageSize: learnerPage.pageSize,
          searchTerm: learnerSearch,
          courseId: learnerCourseId || null,
          status: learnerStatus || null,
        });
        if (!res.success) {
          setLearnerRows([]);
          setLearnerPage((prev) => ({ ...prev, ...emptyPage(), pageSize: prev.pageSize }));
          showError(res.error || res.message || 'Failed to load learner analytics');
          return;
        }
        const payload = unwrapPayload(res);
        setLearnerRows(unwrapList(payload));
        setLearnerPage((prev) => applyPageMeta(prev, payload, pageNumber));
      } catch (err) {
        setLearnerRows([]);
        showError(err.message || 'Failed to load learner analytics');
      } finally {
        setLearnerLoading(false);
      }
    },
    [
      dateFilter,
      learnerPage.pageSize,
      learnerSearch,
      learnerCourseId,
      learnerStatus,
      showError,
    ]
  );

  const fetchQuizAnalytics = useCallback(
    async (pageNumber = 1) => {
      setQuizLoading(true);
      try {
        const res = await apiService.getEducationQuizAnalytics({
          ...dateFilter,
          pageNumber,
          pageSize: quizPage.pageSize,
        });
        if (!res.success) {
          setQuizRows([]);
          setQuizPage((prev) => ({ ...prev, ...emptyPage(), pageSize: prev.pageSize }));
          showError(res.error || res.message || 'Failed to load quiz analytics');
          return;
        }
        const payload = unwrapPayload(res);
        setQuizRows(unwrapList(payload));
        setQuizPage((prev) => applyPageMeta(prev, payload, pageNumber));
      } catch (err) {
        setQuizRows([]);
        showError(err.message || 'Failed to load quiz analytics');
      } finally {
        setQuizLoading(false);
      }
    },
    [dateFilter, quizPage.pageSize, showError]
  );

  const fetchActivity = useCallback(
    async (pageNumber = 1) => {
      setActivityLoading(true);
      try {
        const res = await apiService.getEducationLearningActivity({
          ...dateFilter,
          pageNumber,
          pageSize: activityPage.pageSize,
        });
        if (!res.success) {
          setActivityRows([]);
          setActivityPage((prev) => ({ ...prev, ...emptyPage(), pageSize: prev.pageSize }));
          showError(res.error || res.message || 'Failed to load learning activity');
          return;
        }
        const payload = unwrapPayload(res);
        setActivityRows(unwrapList(payload));
        setActivityPage((prev) => applyPageMeta(prev, payload, pageNumber));
      } catch (err) {
        setActivityRows([]);
        showError(err.message || 'Failed to load learning activity');
      } finally {
        setActivityLoading(false);
      }
    },
    [dateFilter, activityPage.pageSize, showError]
  );

  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFiltersReady(false);
      setError('');
      try {
        const panelErrors = await fetchSummaryAndPanels();
        if (cancelled) return;
        await Promise.all([
          fetchCourseAnalytics(1),
          fetchQuizAnalytics(1),
          fetchActivity(1),
        ]);
        if (!cancelled && panelErrors.length) {
          setError(panelErrors[0]);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load analytics');
      } finally {
        if (!cancelled) {
          setBootLoading(false);
          setFiltersReady(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on dateFilter only — tables reset to page 1 when period changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  useEffect(() => {
    const t = setTimeout(() => setLearnerSearch(learnerSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [learnerSearchInput]);

  useEffect(() => {
    if (!filtersReady) return;
    fetchLearnerAnalytics(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, learnerSearch, learnerCourseId, learnerStatus]);

  const kpiCards = [
    {
      key: 'totalCourses',
      label: 'Total Courses',
      value: formatNumber(summary.totalCourses),
      icon: BookOpen,
    },
    {
      key: 'activeCourses',
      label: 'Active Courses',
      value: formatNumber(summary.activeCourses),
      icon: TrendingUp,
    },
    {
      key: 'totalLearners',
      label: 'Total Learners',
      value: formatNumber(summary.totalLearners),
      icon: Users,
    },
    {
      key: 'completedCourses',
      label: 'Completed Courses',
      value: formatNumber(summary.completedCourses),
      icon: CheckCircle2,
    },
    {
      key: 'averageProgress',
      label: 'Average Progress',
      value: formatPercent(summary.averageProgress),
      icon: BarChart3,
    },
    {
      key: 'completionRate',
      label: 'Completion Rate',
      value: formatPercent(summary.completionRate),
      icon: Percent,
    },
    {
      key: 'quizPassRate',
      label: 'Quiz Pass Rate',
      value: formatPercent(summary.quizPassRate),
      icon: ClipboardList,
    },
    {
      key: 'certificatesIssued',
      label: 'Certificates Issued',
      value: formatNumber(summary.certificatesIssued),
      icon: Award,
    },
  ];

  if (bootLoading) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page edu-page" style={{ '--edu-primary': THEME }}>
      <div className="page-inner">
        <div className="edu-header-row">
          <ModulePageHeader
            icon={BarChart3}
            title="Education Analytics"
            subtitle="Monitor learner engagement, course performance and quiz results."
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
        </div>

        <ModuleAlert message={error} />

        <div className="edu-toolbar">
          <select
            className="edu-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label="Date period"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          {period === 'Custom' ? (
            <>
              <input
                type="date"
                className="edu-input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                aria-label="From date"
              />
              <input
                type="date"
                className="edu-input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                aria-label="To date"
              />
            </>
          ) : null}
        </div>

        <div className="edu-kpi-grid">
          {kpiCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.key} className="module-card edu-kpi">
                <div className="edu-kpi__chip" style={{ background: `${THEME}14`, color: THEME }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="edu-kpi__body">
                  <span className="edu-kpi__label">{card.label}</span>
                  <strong className="edu-kpi__value">{card.value}</strong>
                </div>
              </div>
            );
          })}
        </div>

        <div className="edu-analytics-layout">
          <div className="edu-analytics-main">
            <section className="module-card edu-card">
              <div className="edu-section">
                <h2 className="edu-section-title">Course Performance</h2>
                <p className="edu-section__hint">Enrollment, progress and quiz pass rates by course.</p>
              </div>
              {courseRows.length === 0 && !courseLoading ? (
                <EmptyBlock icon={BookOpen} message="No course performance data for this period." />
              ) : (
                <>
                  <div className="edu-table-wrap">
                    <table className="edu-table">
                      <thead>
                        <tr>
                          <th>Course</th>
                          <th>Status</th>
                          <th>Learners</th>
                          <th>In progress</th>
                          <th>Completed</th>
                          <th>Avg progress</th>
                          <th>Completion</th>
                          <th>Quiz pass</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courseRows.map((row) => (
                          <tr key={row.courseId}>
                            <td className="edu-title-cell">{row.courseName || '—'}</td>
                            <td>
                              <span
                                className={`edu-status ${row.isActive ? 'is-active' : 'is-inactive'}`}
                              >
                                {row.status || (row.isActive ? 'Active' : 'Inactive')}
                              </span>
                            </td>
                            <td>{formatNumber(row.learners)}</td>
                            <td>{formatNumber(row.inProgress)}</td>
                            <td>{formatNumber(row.completed)}</td>
                            <td>{formatPercent(row.averageProgress)}</td>
                            <td>{formatPercent(row.completionRate)}</td>
                            <td>{formatPercent(row.quizPassRate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar
                    pagination={coursePage}
                    loading={courseLoading}
                    onPage={fetchCourseAnalytics}
                  />
                </>
              )}
            </section>

            <section className="module-card edu-card">
              <div className="edu-section">
                <h2 className="edu-section-title">Learners</h2>
                <p className="edu-section__hint">Individual learner progress across your courses.</p>
              </div>
              <div className="edu-toolbar edu-toolbar--inset">
                <input
                  className="edu-input"
                  placeholder="Search learners or courses…"
                  value={learnerSearchInput}
                  onChange={(e) => setLearnerSearchInput(e.target.value)}
                />
                <select
                  className="edu-select"
                  value={learnerCourseId}
                  onChange={(e) => setLearnerCourseId(e.target.value)}
                  aria-label="Course filter"
                >
                  <option value="">All courses</option>
                  {courseOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  className="edu-select"
                  value={learnerStatus}
                  onChange={(e) => setLearnerStatus(e.target.value)}
                  aria-label="Status filter"
                >
                  {LEARNER_STATUSES.map((s) => (
                    <option key={s.value || 'all'} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              {learnerRows.length === 0 && !learnerLoading ? (
                <EmptyBlock icon={Users} message="No learners found for the selected filters." />
              ) : (
                <>
                  <div className="edu-table-wrap">
                    <table className="edu-table">
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Course</th>
                          <th>Progress</th>
                          <th>Status</th>
                          <th>Last activity</th>
                          <th>Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {learnerRows.map((row) => (
                          <tr key={`${row.userId}-${row.courseId}`}>
                            <td className="edu-title-cell">{row.clientName || '—'}</td>
                            <td>{row.courseName || '—'}</td>
                            <td>{formatPercent(row.progressPercent)}</td>
                            <td>
                              <span className={`edu-status ${learnerStatusClass(row.status)}`}>
                                {row.status || '—'}
                              </span>
                            </td>
                            <td>{formatCourseDate(row.lastActivity)}</td>
                            <td>{formatCourseDate(row.completedDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar
                    pagination={learnerPage}
                    loading={learnerLoading}
                    onPage={fetchLearnerAnalytics}
                  />
                </>
              )}
            </section>

            <section className="module-card edu-card">
              <div className="edu-section">
                <h2 className="edu-section-title">Quiz Performance</h2>
                <p className="edu-section__hint">Attempts, pass rates and average scores by quiz.</p>
              </div>
              {quizRows.length === 0 && !quizLoading ? (
                <EmptyBlock icon={ClipboardList} message="No quiz performance data for this period." />
              ) : (
                <>
                  <div className="edu-table-wrap">
                    <table className="edu-table">
                      <thead>
                        <tr>
                          <th>Quiz</th>
                          <th>Course</th>
                          <th>Attempts</th>
                          <th>Learners</th>
                          <th>Passed</th>
                          <th>Failed</th>
                          <th>Pass rate</th>
                          <th>Avg score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quizRows.map((row) => (
                          <tr key={row.lessonId}>
                            <td className="edu-title-cell">{row.quizTitle || '—'}</td>
                            <td>{row.courseName || '—'}</td>
                            <td>{formatNumber(row.totalAttempts)}</td>
                            <td>{formatNumber(row.uniqueLearners)}</td>
                            <td>{formatNumber(row.passedAttempts)}</td>
                            <td>{formatNumber(row.failedAttempts)}</td>
                            <td>{formatPercent(row.passRate)}</td>
                            <td>{formatPercent(row.averageScore)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar
                    pagination={quizPage}
                    loading={quizLoading}
                    onPage={fetchQuizAnalytics}
                  />
                </>
              )}
            </section>

            <section className="module-card edu-card">
              <div className="edu-section">
                <h2 className="edu-section-title">Recent Learning Activity</h2>
                <p className="edu-section__hint">Starts, completions, quiz passes and certificates.</p>
              </div>
              {activityRows.length === 0 && !activityLoading ? (
                <EmptyBlock
                  icon={GraduationCap}
                  message="No learning activity recorded for this period."
                />
              ) : (
                <>
                  <div className="edu-table-wrap">
                    <table className="edu-table">
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Event</th>
                          <th>Course</th>
                          <th>When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityRows.map((row, idx) => (
                          <tr key={`${row.eventType}-${row.occurredAt}-${idx}`}>
                            <td className="edu-title-cell">{row.clientName || '—'}</td>
                            <td>{row.eventLabel || row.eventType || '—'}</td>
                            <td>{row.courseName || '—'}</td>
                            <td>{formatCourseDate(row.occurredAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar
                    pagination={activityPage}
                    loading={activityLoading}
                    onPage={fetchActivity}
                  />
                </>
              )}
            </section>
          </div>

          <aside className="edu-analytics-side">
            <section className="module-card edu-card edu-side-panel">
              <h2 className="edu-section-title">Popular Courses</h2>
              <p className="edu-section__hint">Top courses by active learners.</p>
              {popularCourses.length === 0 ? (
                <EmptyBlock icon={BookOpen} message="No popular courses yet." />
              ) : (
                <ol className="edu-rank-list">
                  {popularCourses.map((item) => (
                    <li key={item.courseId} className="edu-rank-list__item">
                      <span className="edu-rank-list__rank">{item.rank || '—'}</span>
                      <div className="edu-rank-list__body">
                        <strong>{item.courseName || '—'}</strong>
                        <span>{formatNumber(item.learners)} learners</span>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className="module-card edu-card edu-side-panel">
              <h2 className="edu-section-title">Highest Completion</h2>
              <p className="edu-section__hint">Courses with the strongest completion rates.</p>
              {highestCompletion.length === 0 ? (
                <EmptyBlock icon={CheckCircle2} message="No completion rankings yet." />
              ) : (
                <ul className="edu-rank-list">
                  {highestCompletion.map((item) => (
                    <li key={item.courseId} className="edu-rank-list__item">
                      <div className="edu-rank-list__body">
                        <strong>{item.courseName || '—'}</strong>
                        <span>
                          {formatPercent(item.completionRate)} · {formatNumber(item.learners)}{' '}
                          learners
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="module-card edu-card edu-side-panel">
              <h2 className="edu-section-title">Quiz Summary</h2>
              <p className="edu-section__hint">Best and most difficult quizzes in this period.</p>
              {!quizSummary ||
              (!quizSummary.bestPerformingQuiz &&
                !quizSummary.mostDifficultQuiz &&
                !(Number(quizSummary.totalQuizAttempts) > 0)) ? (
                <EmptyBlock icon={ClipboardList} message="No quiz summary data yet." />
              ) : (
                <dl className="edu-quiz-summary-list">
                  <div>
                    <dt>Best performing</dt>
                    <dd>
                      {quizSummary.bestPerformingQuiz || '—'}
                      {quizSummary.bestPassRate != null ? (
                        <span className="edu-muted"> · {formatPercent(quizSummary.bestPassRate)}</span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Most difficult</dt>
                    <dd>
                      {quizSummary.mostDifficultQuiz || '—'}
                      {quizSummary.lowestPassRate != null ? (
                        <span className="edu-muted">
                          {' '}
                          · {formatPercent(quizSummary.lowestPassRate)}
                        </span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Average quiz score</dt>
                    <dd>{formatPercent(quizSummary.averageQuizScore)}</dd>
                  </div>
                  <div>
                    <dt>Total attempts</dt>
                    <dd>{formatNumber(quizSummary.totalQuizAttempts)}</dd>
                  </div>
                </dl>
              )}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default EducationAnalytics;
